import {
  IAccount,
  IActivity,
  IApiSessionResponse,
  IAuthorizationListResponse,
  IAuthorizedAccountsResponse,
  IClient,
  IMember,
  IMemberAdvocate,
  INewUser,
  IOccupation,
  IPublicUserResponse,
  IUserByEmailResponse,
  IUserTagResponse
} from "@wingspanhq/users";
import { AxiosError } from "axios";
import { InfiniteQueryConfig, QueryConfig } from "react-query";
import { ILinkTokenResponse, usersService } from "../../services/users";
import { useWSInfiniteQuery, useWSQuery } from "../helpers";
import {
  QUERY_ADVOCATE,
  QUERY_API_SESSION,
  QUERY_API_SESSION_LIST,
  QUERY_AUTHORIZATION_SCOPE_GROUPS,
  QUERY_AUTHORIZATIONS,
  QUERY_AUTHORIZED_ACCOUNTS,
  QUERY_OCCUPATIONS,
  QUERY_USERS_ACCOUNT,
  QUERY_USERS_ACCOUNT_LINK_TOKEN,
  QUERY_USERS_ACCOUNTS,
  QUERY_USERS_ACTIVITIES,
  QUERY_USERS_CLIENT,
  QUERY_USERS_MEMBER_PROFILE,
  QUERY_USERS_ORGANIZATION_USERS,
  QUERY_USERS_PUBLIC_USER_ID,
  QUERY_USERS_USER_PROFILE,
  QUERY_USERS_USER_TAG
} from "./keys";
import { IWithholdingTransactionResponse } from "@wingspanhq/bookkeeping/dist/lib/interfaces/withholdingTransaction";
import { QUERY_TAXES_WITHHOLDING_TRANSACTIONS } from "../taxes/keys";
import bookkeepingService from "../../services/bookkeeping";

export const useUserProfile = (
  userId: string,
  queryConfig?: QueryConfig<INewUser, AxiosError>
) => {
  return useWSQuery(
    QUERY_USERS_USER_PROFILE,
    async () => await usersService.user.get(userId),
    queryConfig
  );
};

export const useActivities = (
  userId: string,
  queryConfig?: QueryConfig<IActivity, AxiosError>
) => {
  return useWSQuery(
    QUERY_USERS_ACTIVITIES,
    async () => {
      try {
        return await usersService.activity.get(userId);
      } catch {
        const defaultData: IActivity = {
          events: {
            incomeReviewedAt: new Date(0),
            expensesReviewedAt: new Date(0)
          },
          flows: {},
          userId
        };

        return defaultData;
      }
    },
    queryConfig
  );
};

export const useMemberProfile = (
  memberId: string,
  queryConfig?: QueryConfig<IMember, AxiosError>
) => {
  return useWSQuery(
    QUERY_USERS_MEMBER_PROFILE,
    async () => {
      return await usersService.member.get(memberId);
    },

    queryConfig
  );
};

export const useAdvocate = (
  advocateId: string,
  queryConfig?: QueryConfig<IMemberAdvocate, AxiosError>
) => {
  return useWSQuery(
    QUERY_ADVOCATE,
    async () => {
      return await usersService.advocate.get(advocateId);
    },
    {
      enabled: advocateId,
      ...queryConfig
    }
  );
};

export const useClientQuery = (
  clientId: string,
  queryConfig?: QueryConfig<IClient, AxiosError>
) =>
  useWSQuery(QUERY_USERS_CLIENT, () => usersService.client.get(clientId), {
    retry: false,
    ...queryConfig
  });

export const useAccount = (
  accountId: string,
  queryConfig?: QueryConfig<IAccount, AxiosError>
) => {
  return useWSQuery(
    QUERY_USERS_ACCOUNT,
    () => usersService.account.get(accountId),
    queryConfig
  );
};

export const useAccounts = (queryConfig?: QueryConfig<IAccount[], unknown>) => {
  return useWSQuery(
    QUERY_USERS_ACCOUNTS,
    usersService.account.list,
    queryConfig
  );
};

export const useOccupationsQuery = (
  queryConfig?: QueryConfig<IOccupation[], AxiosError>
) => {
  return useWSQuery(
    QUERY_OCCUPATIONS,
    usersService.user.occupation.list,
    queryConfig
  );
};

export const useUserTag = (
  tag: string,
  queryConfig?: QueryConfig<IUserTagResponse, AxiosError>
) =>
  useWSQuery(
    QUERY_USERS_USER_TAG,
    () => usersService.user.tag.get(tag),
    queryConfig
  );

export const useUserByEmail = (
  email: string,
  queryConfig?: QueryConfig<IUserByEmailResponse, AxiosError>
) =>
  useWSQuery(
    QUERY_USERS_USER_TAG,
    () => usersService.user.email.get(email),
    queryConfig
  );

export const useUserById = (
  id: string,
  queryConfig?: QueryConfig<IPublicUserResponse, AxiosError>
) =>
  useWSQuery(
    QUERY_USERS_PUBLIC_USER_ID,
    () => usersService.user.public.get(id),
    queryConfig
  );

export const useAuthorizedAccounts = (
  userId: string,
  queryConfig?: QueryConfig<IAuthorizedAccountsResponse, AxiosError>
) =>
  useWSQuery(
    QUERY_AUTHORIZED_ACCOUNTS,
    () => usersService.authorization.accounts(userId),
    queryConfig
  );

export const useAuthorizations = (
  queryConfig?: InfiniteQueryConfig<IAuthorizationListResponse, AxiosError>
) =>
  useWSInfiniteQuery(
    QUERY_AUTHORIZATIONS,
    async (query, pageNumber = 1) => {
      return await usersService.authorization.list({
        page: {
          size: 10,
          number: pageNumber
        }
      });
    },
    {
      getFetchMore: (lastPage, allPages) => {
        if (lastPage.length < 10) {
          return undefined;
        }

        return allPages.length + 1;
      },
      ...queryConfig
    }
  );

export const useAuthorizedScopeGroupsQuery = (
  requestingUserId: string,
  queryConfig?: QueryConfig<IAuthorizedAccountsResponse, AxiosError>
) =>
  useWSQuery<IAuthorizedAccountsResponse, AxiosError>(
    QUERY_AUTHORIZATION_SCOPE_GROUPS,
    () => usersService.authorization.scopeGroups.list(requestingUserId),
    queryConfig
  );

export const useAPISessionList = (
  queryConfig?: QueryConfig<IApiSessionResponse[], AxiosError>
) =>
  useWSQuery(
    QUERY_API_SESSION_LIST,
    usersService.session.apiToken.list,
    queryConfig
  );

export const useGetAPISession = (
  id: string,
  queryConfig?: QueryConfig<IApiSessionResponse, AxiosError>
) =>
  useWSQuery(
    `${QUERY_API_SESSION}-${id}`,
    () => usersService.session.apiToken.get(id),
    queryConfig
  );

export const useUserAccountLinkToken = (
  queryConfig?: QueryConfig<ILinkTokenResponse, AxiosError>
) =>
  useWSQuery(
    QUERY_USERS_ACCOUNT_LINK_TOKEN,
    () => usersService.accountLink.create(),
    {
      ...queryConfig,
      retry: 1
    }
  );

export const useOrganizationUsers = (
  queryConfig?: InfiniteQueryConfig<INewUser[], unknown>
) =>
  useWSInfiniteQuery(
    QUERY_USERS_ORGANIZATION_USERS,
    async (query, pageNumber = 1) => {
      return await usersService.organization.users.list({
        page: {
          size: 10,
          number: pageNumber
        }
      });
    },
    {
      getFetchMore: (lastPage, allPages) => {
        if (lastPage.length < 10) {
          return undefined;
        }

        return allPages.length + 1;
      },
      ...queryConfig
    }
  );
